import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { currentLevel } from '../../utils/currentLevel';

// Levels in order: trampoline 1, 2, 2a, 2b, 3… then double mini D1, D2…
const DISCIPLINE_ORDER = { TRA: 0, DMT: 1, TUM: 2 };
const DISCIPLINE_NAMES = { TRA: 'Trampoline', DMT: 'Double mini', TUM: 'Tumbling' };
const levelNumber = l => parseInt(String(l.identifier).replace(/^\D+/, ''), 10) || 0;
const levelOrder = (a, b) =>
  (DISCIPLINE_ORDER[a.discipline || 'TRA'] - DISCIPLINE_ORDER[b.discipline || 'TRA'])
  || (levelNumber(a) - levelNumber(b)) || a.identifier.localeCompare(b.identifier);

// The skills for a level, ticked when done. Opens on the level the gymnast is
// working on; they can pick any other level to look at and come back.
export default function ChildLevel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pickedId, setPickedId] = useState(null);

  useEffect(() => {
    Promise.all([axios.get(`/api/gymnasts/${user.id}`), axios.get('/api/levels')])
      .then(([g, l]) => setData({ gymnast: g.data, levels: l.data }))
      .catch(() => setError('Could not load your level.'));
  }, [user.id]);

  if (error) return <><p className="bk-error">{error}</p></>;
  if (!data) return <div className="loading"><div className="spinner" /></div>;

  const levels = [...(data.levels || [])].sort(levelOrder);
  const working = currentLevel(data.gymnast, data.levels);
  const levelDone = new Set((data.gymnast.levelProgress || [])
    .filter(lp => lp.status === 'COMPLETED').map(lp => lp.levelId || lp.level?.id));
  const level = levels.find(l => l.id === pickedId) || working || levels[levels.length - 1];
  const viewingOther = working && level && level.id !== working.id;
  const groups = Object.keys(DISCIPLINE_ORDER)
    .map(d => ({ d, items: levels.filter(l => (l.discipline || 'TRA') === d) }))
    .filter(g => g.items.length > 0);
  const optionLabel = l => `Level ${l.identifier}: ${l.name}${l.id === working?.id ? ' (working on)' : ''}${levelDone.has(l.id) ? ' ✓' : ''}`;
  const picker = levels.length > 1 && (
    <div className="child-level-picker">
      {viewingOther && (
        <button type="button" className="child-level-picker__back" onClick={() => setPickedId(null)}>
          Back to my level ({working.identifier})
        </button>
      )}
      <label className="child-level-picker__label">
        Look at another level
        <select value={level?.id || ''} onChange={e => setPickedId(e.target.value)}>
          {groups.map(g => (groups.length > 1 ? (
            <optgroup key={g.d} label={DISCIPLINE_NAMES[g.d]}>
              {g.items.map(l => <option key={l.id} value={l.id}>{optionLabel(l)}</option>)}
            </optgroup>
          ) : g.items.map(l => <option key={l.id} value={l.id}>{optionLabel(l)}</option>)))}
        </select>
      </label>
    </div>
  );
  if (!level) {
    return <><h1 className="child-app__title">My level</h1><p>There are no levels set up yet.</p></>;
  }
  // Done on trampoline isn't done on double mini, so only this level's apparatus counts
  const done = new Set((data.gymnast.skillProgress || [])
    .filter(sp => sp.status === 'COMPLETED' && (sp.apparatus || 'TRA') === (level.discipline || 'TRA'))
    .map(sp => sp.skill?.id || sp.skillId));
  const skills = level.skills || [];
  const doneCount = skills.filter(s => done.has(s.id)).length;
  const routines = level.routines || [];
  const isDmt = level.discipline === 'DMT';
  const routineDone = new Set((data.gymnast.routineProgress || [])
    .filter(rp => rp.status === 'COMPLETED').map(rp => rp.routineId || rp.routine?.id));

  return (
    <>
      {picker}
      <h1 className={`child-app__title${level.id === working?.id ? ' child-app__title--mine' : ''}`}>Level {level.identifier}: {level.name}</h1>
      <p className="child-app__muted">
        {doneCount} of {skills.length} skills done
        {level.id === working?.id && ' · the level you are working on'}
        {levelDone.has(level.id) && ' · level complete'}
      </p>
      {!working && level.id === levels[levels.length - 1].id && <p>You have completed every level. Well done!</p>}
      {skills.length === 0 ? <p>This level has no skills listed yet.</p> : (
        <ul className="child-list">
          {skills.map(s => (
            <li key={s.id} className={done.has(s.id) ? 'is-done' : undefined}>
              <span className="child-list__mark" aria-hidden="true">{done.has(s.id) ? '✓' : ''}</span>
              <span>{s.name}</span>
              <span className="visually-hidden">{done.has(s.id) ? ' (done)' : ' (not done yet)'}</span>
            </li>
          ))}
        </ul>
      )}

      {routines.length > 0 && (
        <>
          <h2 className="child-app__subtitle">{isDmt ? (routines.length === 1 ? 'Pass' : 'Passes') : (routines.length === 1 ? 'Routine' : 'Routines')}</h2>
          {isDmt && level.minPassTariff != null
            ? <p className="child-app__muted">These are examples. Any two passes of {Number(level.minPassTariff).toFixed(1)} or more complete this level.</p>
            : routines.length > 1 && <p className="child-app__muted">{isDmt ? 'Learn all of these.' : 'Learn any one of these.'}</p>}
          {routines.map((r, i) => (
            <section key={r.id} className="child-routine" aria-label={r.name || `Routine ${i + 1}`}>
              <h3 className="child-routine__name">
                {r.name || `Routine ${i + 1}`}
                {routineDone.has(r.id) && <span className="child-routine__done"> ✓ Done</span>}
              </h3>
              <ol className="child-list child-routine__skills">
                {(r.skills || []).map(sk => (
                  <li key={sk.id}>
                    <span>{sk.name}</span>
                    {sk.figNotation && <code className="child-routine__fig">{sk.figNotation}</code>}
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </>
      )}
    </>
  );
}
